import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { RadioCard, RadioCardGroup } from "@/components/ui/RadioCard";
import {
  AvailabilityBadge,
  KeySetPicker,
  keySetChoiceReady,
  RadioRows,
  useCustomerManagedKeys,
  useResolveKeySet,
  type KeySetChoice,
} from "@/lib/remote-identity";
import { useRoutes } from "@/routes";
import { useGramContext } from "@gram/client/react-query/_context.js";
import { buildOrganizationRemoteSessionClientsQuery } from "@gram/client/react-query/organizationRemoteSessionClients.js";
import { Bot, User } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { Text } from "@/components/ui/Text";
import { useIsPlatformAdmin, useOrganization } from "@/contexts/Auth";
import { useFetcher } from "@/contexts/Fetcher";
import { useSdkClient } from "@/contexts/Sdk";
import {
  proxyRegisterUpstreamClient,
  registrationProvenance,
} from "@/lib/proxyRegisterUpstreamClient";
import type { RemoteSessionIssuer } from "@gram/client/models/components/remotesessionissuer.js";
import { CreateRemoteSessionClientFormTokenEndpointAuthMethod } from "@gram/client/models/components/createremotesessionclientform.js";
import { invalidateAllOrganizationRemoteSessionClients } from "@gram/client/react-query/organizationRemoteSessionClients.js";
import { invalidateAllOrganizationRemoteSessionIssuers } from "@gram/client/react-query/organizationRemoteSessionIssuers.js";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Stack } from "@/components/ui/Stack";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ClientTypeFields,
  OverridesFields,
} from "../mcp/x/tabs/settings/sections/authentication/IssuerFormFields";
import {
  availableClientTypes,
  type ClientType,
  dynamicClientRegistrationAvailability,
  narrowTokenEndpointAuthMethod,
  parseScopes,
  TUNNELED_DCR_PERMISSION_MESSAGE,
} from "../mcp/x/tabs/settings/sections/authentication/issuerFormUtils";
import { issuerDocumentationLinks } from "./issuerDocumentationLinks";

// Sentinel for the unselected project in the org-level-issuer scope picker.
// Radix Select treats the empty string specially, so submission is gated until
// the operator picks a real project (a client must be project-scoped).
const UNSELECTED_PROJECT = "";

// DocumentationLinks surfaces the issuer's documentation so customers can create
// the upstream OAuth client themselves, owning its credentials, access, and rate
// limits rather than sharing a Speakeasy-owned client. Renders nothing when the
// issuer carries no documentation URLs.
function DocumentationLinks({ issuer }: { issuer: RemoteSessionIssuer }) {
  const links = issuerDocumentationLinks(issuer);

  if (links.length === 0) {
    return null;
  }

  return (
    <Stack gap={2}>
      <Text muted small>
        Documentation links to assist with creating this client.
      </Text>
      <Stack direction="horizontal" gap={4}>
        {links.map(({ label, url }) => (
          <Text key={label} muted small>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground underline underline-offset-2"
            >
              {label}
            </a>
          </Text>
        ))}
      </Stack>
    </Stack>
  );
}

// CreateRemoteSessionClientSheet registers a standalone remote_session_client
// under the page's already-selected issuer via the org-admin createClient
// endpoint, with no user_session_issuer attachments. It is a pared-down sibling
// of the MCP Authentication tab's AttachRemoteIdentityProviderSheet: the issuer
// is fixed, so it skips the issuer-resolution half and reuses only the
// credentials half (DCR-vs-manual, token endpoint auth method, scope/audience
// overrides). A project-specific issuer's client inherits the issuer's project;
// an organization-level issuer has no project to inherit, so the operator must
// downscope the client to a project in the organization.
export function CreateRemoteSessionClientSheet({
  open,
  onOpenChange,
  issuer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issuer: RemoteSessionIssuer;
}): JSX.Element {
  const queryClient = useQueryClient();
  const organization = useOrganization();
  const isPlatformAdmin = useIsPlatformAdmin();
  const { fetch: authedFetch } = useFetcher();
  const client = useSdkClient();

  // DCR is offered when the issuer has a discovered or manually configured
  // registration_endpoint (RFC 7591); otherwise the operator pastes a client_id
  // and optional client_secret obtained out-of-band (Manual).
  const registrationEndpoint = issuer.registrationEndpoint?.trim() ?? "";
  const {
    available: dcrAvailable,
    permissionRestricted: tunneledDcrRestricted,
  } = dynamicClientRegistrationAvailability({
    registrationEndpoint,
    tunneled: !!issuer.tunneledMcpServerId,
    isPlatformAdmin,
  });

  // CIMD is offered only when the issuer advertises support for Client ID
  // Metadata Documents (parsed during issuer discovery); the platform then
  // hosts the document and uses its URL as the client_id, so no credentials are
  // collected.
  const cimdAvailable = issuer.clientIdMetadataDocumentSupported;

  // The selectable client types for this issuer. DCR and CIMD appear only when
  // the issuer supports them; Manual is always available. The first entry is
  // the default (an automatic type when one is available).
  const clientTypes = useMemo(
    () => availableClientTypes({ dcrAvailable, cimdAvailable }),
    [dcrAvailable, cimdAvailable],
  );
  const defaultClientType = clientTypes[0] ?? "manual";

  // Organization-level issuers (no owning project) have no project to inherit,
  // so the operator must name one; project-specific issuers inherit silently.
  const isOrganizational = !issuer.projectId;

  const projects = organization.projects;
  const issuerProjectSlug = projects.find(
    (project) => project.id === issuer.projectId,
  )?.slug;

  // For an organization-level issuer the client is either created at the
  // organization level (no project, attachable by every project) or downscoped
  // to a single project. Defaults to organization-level, the issuer's own scope.
  const [scope, setScope] = useState<"organization" | "project">(
    "organization",
  );
  const [projectId, setProjectId] = useState<string>(UNSELECTED_PROJECT);
  const [clientType, setClientType] = useState<ClientType>(defaultClientType);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [tokenEndpointAuthMethod, setTokenEndpointAuthMethod] = useState<
    CreateRemoteSessionClientFormTokenEndpointAuthMethod | ""
  >("");
  const [scopeOverride, setScopeOverride] = useState("");
  const [audienceOverride, setAudienceOverride] = useState("");

  // Who the upstream credential belongs to, fixed at creation. A service
  // account only needs the token endpoint, so a provider without one can't
  // offer it.
  const routes = useRoutes();
  const gramClient = useGramContext();
  const { entitled: customerManagedKeys } = useCustomerManagedKeys();
  const resolveKeySet = useResolveKeySet();
  const hasTokenEndpoint = !!issuer.tokenEndpoint?.trim();
  const [owner, setOwner] = useState<"subject" | "self">("subject");
  const [selfMethod, setSelfMethod] = useState<
    "client_secret_basic" | "client_secret_post" | "private_key_jwt"
  >("client_secret_basic");
  const [keySet, setKeySet] = useState<KeySetChoice | null>(null);
  const [assertionAudience, setAssertionAudience] = useState<
    "issuer" | "token_endpoint"
  >("issuer");
  const selfKey = owner === "self" && selfMethod === "private_key_jwt";

  // Suggest the key set another client at this provider already signs with.
  const siblingsQuery = buildOrganizationRemoteSessionClientsQuery(gramClient, {
    issuerId: issuer.id,
  });
  const { data: siblingsData, isLoading: siblingsLoading } = useQuery({
    ...siblingsQuery,
    queryKey: [...siblingsQuery.queryKey, { organizationId: organization.id }],
    enabled: open && selfKey,
  });
  const suggestedKeySetId =
    (siblingsData?.result.items ?? [])
      .map((item) => item.client)
      .find((sibling) => sibling.jsonWebKeySetId != null)?.jsonWebKeySetId ??
    null;

  // One mutation drives all client types. Its mutationFn does whatever async
  // work the chosen type needs — a DCR proxy registration pre-step, or a direct
  // create — so isPending/error are a single source of truth (no separate
  // registering flag or per-type mutation to combine). It returns the
  // unsupported DCR auth method, if any, for the success toast.
  const createMutation = useMutation({
    mutationFn: async (): Promise<{
      unsupportedDcrAuthMethod: string | null;
      serviceAccountId?: string;
    }> => {
      const parsedScopes = parseScopes(scopeOverride);
      const trimmedAudience = audienceOverride.trim();
      // Project-specific issuers inherit their project (project_id omitted). For
      // an org-level issuer, omitting project_id creates an organization-level
      // client (no project); sending it downscopes to the chosen project.
      const projectIdForOrg =
        isOrganizational && scope === "project" ? projectId : undefined;

      // A service account always pastes what the provider issued; the key set
      // is created first when it is a new one.
      if (owner === "self") {
        const jsonWebKeySetId =
          selfKey && keySet ? await resolveKeySet(keySet) : undefined;
        const created = await client.organizationRemoteSessionClients.create({
          createOrganizationRemoteSessionClientForm: {
            remoteSessionIssuerId: issuer.id,
            projectId: projectIdForOrg,
            clientId: clientId.trim(),
            credentialOwner: "self",
            tokenEndpointAuthMethod: selfMethod,
            clientSecret: selfKey ? undefined : clientSecret.trim(),
            jsonWebKeySetId,
            tokenEndpointAuthAudienceFormat: selfKey
              ? assertionAudience
              : undefined,
            scope: parsedScopes.length > 0 ? parsedScopes : undefined,
            audience: trimmedAudience || undefined,
          },
        });
        return { unsupportedDcrAuthMethod: null, serviceAccountId: created.id };
      }

      // CIMD: the platform generates the client_id and hosts the metadata
      // document, so there are no credentials to collect.
      if (clientType === "cimd") {
        await client.organizationRemoteSessionClients.createCimd({
          createCimdOrganizationRemoteSessionClientForm: {
            remoteSessionIssuerId: issuer.id,
            projectId: projectIdForOrg,
            scope: parsedScopes.length > 0 ? parsedScopes : undefined,
            audience: trimmedAudience || undefined,
          },
        });
        return { unsupportedDcrAuthMethod: null };
      }

      // DCR proxies a registration to the upstream issuer for a fresh
      // client_id / client_secret; Manual uses what the operator typed.
      let credentials: {
        clientId: string;
        clientSecret?: string;
        tokenEndpointAuthMethod?: CreateRemoteSessionClientFormTokenEndpointAuthMethod;
        // Set for DCR so the server records when the issuer expires the
        // client and can re-register it in place.
        provenance?: ReturnType<typeof registrationProvenance>;
      };
      let unsupportedDcrAuthMethod: string | null = null;
      if (clientType === "dcr") {
        const registered = await proxyRegisterUpstreamClient(authedFetch, {
          registrationEndpoint,
          // RFC 7591 §2: scope is a space-separated string at registration time.
          scope: parsedScopes.length > 0 ? parsedScopes.join(" ") : undefined,
          tokenEndpointAuthMethod: tokenEndpointAuthMethod || undefined,
          tunneledMcpServerId: issuer.tunneledMcpServerId,
          projectSlug: issuerProjectSlug,
        });
        const narrowedDcrMethod = narrowTokenEndpointAuthMethod(
          registered.tokenEndpointAuthMethod,
        );
        if (registered.tokenEndpointAuthMethod && !narrowedDcrMethod) {
          unsupportedDcrAuthMethod = registered.tokenEndpointAuthMethod;
        }
        credentials = {
          clientId: registered.clientId,
          clientSecret: registered.clientSecret || undefined,
          tokenEndpointAuthMethod:
            narrowedDcrMethod ?? (tokenEndpointAuthMethod || undefined),
          provenance: registrationProvenance(registered),
        };
      } else {
        credentials = {
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim() || undefined,
          tokenEndpointAuthMethod: tokenEndpointAuthMethod || undefined,
        };
      }

      await client.organizationRemoteSessionClients.create({
        createOrganizationRemoteSessionClientForm: {
          remoteSessionIssuerId: issuer.id,
          projectId: projectIdForOrg,
          clientId: credentials.clientId,
          clientSecret: credentials.clientSecret,
          tokenEndpointAuthMethod: credentials.tokenEndpointAuthMethod,
          scope: parsedScopes.length > 0 ? parsedScopes : undefined,
          audience: trimmedAudience || undefined,
          ...credentials.provenance,
        },
      });

      return { unsupportedDcrAuthMethod };
    },
    onSuccess: async ({ unsupportedDcrAuthMethod, serviceAccountId }) => {
      await Promise.all([
        invalidateAllOrganizationRemoteSessionClients(queryClient, {
          refetchType: "all",
        }),
        // The issuer listing carries a per-issuer client count, so creating a
        // client makes that number stale. It shows on the same screen as this
        // sheet when the operator starts from the provider list's Add Client
        // action, and on back-navigation from the Clients tab.
        invalidateAllOrganizationRemoteSessionIssuers(queryClient, {
          refetchType: "all",
        }),
      ]);
      toast.success("Client created");
      if (unsupportedDcrAuthMethod) {
        toast.warning(
          `The issuer registered the client with an unsupported token endpoint auth method (${unsupportedDcrAuthMethod}); it was not stored on the client.`,
        );
      }
      onOpenChange(false);
      // The key set URL to register with the provider lives on the client.
      if (serviceAccountId) {
        routes.remoteIdentityProviders.clientDetail.goTo(
          issuer.id,
          serviceAccountId,
        );
      }
    },
    onError: (error) => {
      // useMutation surfaces error.message via createMutation.error (shown in
      // the inline Alert); a console line keeps the stack for debugging.
      console.error("Create remote session client failed", error);
    },
  });

  const submitting = createMutation.isPending;
  const submitError = createMutation.error
    ? createMutation.error instanceof Error && createMutation.error.message
      ? createMutation.error.message
      : "An unexpected error occurred. Please try again."
    : null;
  const { reset: resetCreateMutation } = createMutation;

  // Reset transient state whenever the sheet is reopened so a prior draft never
  // leaks into a new creation.
  useEffect(() => {
    if (!open) return;
    setScope("organization");
    setProjectId(UNSELECTED_PROJECT);
    setClientId("");
    setClientSecret("");
    setTokenEndpointAuthMethod("");
    setScopeOverride("");
    setAudienceOverride("");
    setClientType(defaultClientType);
    setOwner("subject");
    setSelfMethod("client_secret_basic");
    setKeySet(null);
    setAssertionAudience("issuer");
    resetCreateMutation();
  }, [open, defaultClientType, resetCreateMutation]);

  const projectChosen =
    !isOrganizational ||
    scope === "organization" ||
    projectId !== UNSELECTED_PROJECT;
  // Manual requires a client_id; DCR mints one during proxy registration.
  const credentialsReady =
    clientType === "manual" ? clientId.trim().length > 0 : true;
  const tunnelRouteReady =
    clientType !== "dcr" ||
    !issuer.tunneledMcpServerId ||
    issuerProjectSlug !== undefined;
  const selfReady =
    clientId.trim() !== "" &&
    (selfKey ? keySetChoiceReady(keySet) : clientSecret.trim() !== "");
  const submittable =
    projectChosen &&
    (owner === "self"
      ? hasTokenEndpoint && selfReady
      : credentialsReady &&
        tunnelRouteReady &&
        clientTypes.includes(clientType));

  const handleSubmit = () => {
    if (!submittable || submitting) return;
    createMutation.mutate();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-[560px] flex-col sm:max-w-[560px]"
      >
        <SheetHeader className="px-6 pt-6 pb-0">
          <SheetTitle className="text-lg font-semibold">New Client</SheetTitle>
          <DocumentationLinks issuer={issuer} />
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          <Stack gap={4}>
            {isOrganizational ? (
              <Stack gap={4}>
                <Stack gap={2}>
                  <Label className="text-muted-foreground text-xs">Scope</Label>
                  <Select
                    value={scope}
                    onValueChange={(value) =>
                      setScope(value as "organization" | "project")
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="organization">
                        All projects (organization-level)
                      </SelectItem>
                      <SelectItem value="project">Specific project</SelectItem>
                    </SelectContent>
                  </Select>
                  <Text muted small>
                    An organization-level client has no project and can be
                    attached by every project in the organization.
                  </Text>
                </Stack>

                {scope === "project" && (
                  <Stack gap={2}>
                    <Label className="text-muted-foreground text-xs">
                      Project
                    </Label>
                    <Select value={projectId} onValueChange={setProjectId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a project" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Text muted small>
                      The client will be scoped to this project in the
                      organization.
                    </Text>
                  </Stack>
                )}
              </Stack>
            ) : (
              <Text muted small>
                The client will be created in this authorization server's
                project.
              </Text>
            )}

            <Stack gap={2}>
              <Label className="text-muted-foreground text-xs">
                Signs in as
              </Label>
              <RadioCardGroup
                aria-label="Signs in as"
                size="sm"
                showIndicator={false}
                orientation="horizontal"
                value={owner}
                onValueChange={(next) => setOwner(next as "subject" | "self")}
                className="grid-cols-1 sm:grid-cols-2"
              >
                <RadioCard
                  value="subject"
                  leading={<User className="size-4" />}
                  title="Each user"
                >
                  Every caller connects their own {issuer.name} account.
                </RadioCard>
                <RadioCard
                  value="self"
                  disabled={!hasTokenEndpoint}
                  leading={<Bot className="size-4" />}
                  title="Service account"
                >
                  The client gets one token for itself, and every caller shares
                  it.
                </RadioCard>
              </RadioCardGroup>
              {!hasTokenEndpoint && (
                <Text muted small>
                  {issuer.name} publishes no token endpoint, so a service
                  account client cannot get a token. Add one in Settings to
                  enable it.
                </Text>
              )}
              <Text muted small>
                Fixed once the client is created.
              </Text>
            </Stack>

            {owner === "self" ? (
              <Stack gap={4} className="border-t pt-5">
                <Text muted small>
                  Create a machine-to-machine app in {issuer.name} that is
                  allowed the client credentials grant, then paste its
                  credentials here. Speakeasy requests tokens from{" "}
                  {issuer.tokenEndpoint}. Turn off DPoP for the app: Speakeasy
                  doesn&apos;t send DPoP proofs.
                </Text>
                <Stack gap={2}>
                  <Label
                    htmlFor="self-client-id"
                    className="text-muted-foreground text-xs"
                  >
                    Client ID
                  </Label>
                  <Input
                    id="self-client-id"
                    value={clientId}
                    onChange={setClientId}
                    placeholder="client_abc123"
                    className="font-mono"
                  />
                </Stack>
                <Stack gap={2}>
                  <Label className="text-muted-foreground text-xs">
                    Token Endpoint Auth Method
                  </Label>
                  <RadioRows
                    label="Token Endpoint Auth Method"
                    value={selfMethod}
                    onValueChange={(next) =>
                      setSelfMethod(next as typeof selfMethod)
                    }
                    rows={[
                      {
                        value: "client_secret_basic",
                        title: "client_secret_basic",
                        description:
                          "Secret in the Authorization header (default)",
                        mono: true,
                      },
                      {
                        value: "client_secret_post",
                        title: "client_secret_post",
                        description: "Secret in the request body",
                        mono: true,
                      },
                      {
                        value: "private_key_jwt",
                        title: "private_key_jwt",
                        description:
                          "Assertion signed with one of your key sets",
                        mono: true,
                        disabled: !customerManagedKeys,
                        badge: customerManagedKeys ? undefined : (
                          <AvailabilityBadge>Requires KMS</AvailabilityBadge>
                        ),
                      },
                    ]}
                  />
                </Stack>
                {selfKey ? (
                  <Stack gap={4}>
                    <KeySetPicker
                      value={keySet}
                      onChange={setKeySet}
                      suggestedSetId={suggestedKeySetId}
                      suggestionLoading={siblingsLoading}
                      newSetPlaceholder={`${issuer.name} signing key`}
                      disabled={submitting}
                    />
                    <Text muted small>
                      Register the key set&apos;s public keys with {issuer.name}{" "}
                      after you create the client: its key set URL is on the
                      client&apos;s page.
                    </Text>
                    <Stack gap={2}>
                      <Label className="text-muted-foreground text-xs">
                        Client assertion audience
                      </Label>
                      <Select
                        value={assertionAudience}
                        onValueChange={(next) =>
                          setAssertionAudience(
                            next as "issuer" | "token_endpoint",
                          )
                        }
                      >
                        <SelectTrigger aria-label="Client assertion audience">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="issuer">
                            Issuer URL (default)
                          </SelectItem>
                          <SelectItem value="token_endpoint">
                            Token endpoint URL
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack gap={2}>
                    <Label
                      htmlFor="self-client-secret"
                      className="text-muted-foreground text-xs"
                    >
                      Client Secret
                    </Label>
                    <Input
                      id="self-client-secret"
                      type="password"
                      reveal
                      value={clientSecret}
                      onChange={setClientSecret}
                      placeholder="Paste the client secret"
                    />
                  </Stack>
                )}
              </Stack>
            ) : (
              <ClientTypeFields
                availableTypes={clientTypes}
                clientType={clientType}
                onClientTypeChange={setClientType}
                clientId={clientId}
                clientSecret={clientSecret}
                tokenEndpointAuthMethod={tokenEndpointAuthMethod}
                onClientIdChange={setClientId}
                onClientSecretChange={setClientSecret}
                onTokenEndpointAuthMethodChange={setTokenEndpointAuthMethod}
              />
            )}

            {owner === "subject" && tunneledDcrRestricted && (
              <Text muted small>
                {TUNNELED_DCR_PERMISSION_MESSAGE}
              </Text>
            )}

            <OverridesFields
              scopeOverride={scopeOverride}
              audienceOverride={audienceOverride}
              onScopeOverrideChange={setScopeOverride}
              onAudienceOverrideChange={setAudienceOverride}
            />
          </Stack>

          {submitError && (
            <Alert variant="error" dismissible={false}>
              {submitError}
            </Alert>
          )}
        </div>

        <SheetFooter className="flex-row items-center justify-end gap-2 border-t px-6 py-4">
          <Button
            variant="secondary"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            <Button.Text>Cancel</Button.Text>
          </Button>
          <Button
            variant="primary"
            disabled={!submittable || submitting}
            onClick={handleSubmit}
          >
            <Button.Text>{submitting ? "Creating…" : "Create"}</Button.Text>
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
